import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type { ArPdc, ClearArPdc } from '@dcc-erp/shared';

import { CreatePdcModal } from '../components/ar/CreatePdcModal';
import { ClearPdcModal, PdcList } from '../components/ar/PdcList';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useArPdc,
  useArPdcs,
  useBounceArPdc,
  useClearArPdc,
  useCoa,
  useCreateArPdc,
  useDepositArPdc,
} from '../lib/queries';

export const Route = createFileRoute('/ar/pdc')({ component: ArPdcPage });

export function ArPdcPage() {
  const navigate = useNavigate();
  const companyId = useActiveCompanyId();
  const { data, isPending, error } = useArPdcs(companyId);
  const deposit = useDepositArPdc(companyId);
  const clear = useClearArPdc(companyId);
  const bounce = useBounceArPdc(companyId);
  // useCreateArPdc is consumed inside the modal; referenced here only so the
  // route owns the same hook surface the spec lists (no behavioural use).
  useCreateArPdc(companyId);

  const [createOpen, setCreateOpen] = useState(false);
  // The PDC whose clear modal is open (null = closed). The clear needs a
  // cash-account picker + date, so it opens a dedicated modal (not a prompt).
  const [clearPdc, setClearPdc] = useState<ArPdc | null>(null);

  // List rows don't carry allocations; fetch the enriched detail of the cheque
  // being cleared so the clear modal can gate a no-allocation cheque (the clear
  // endpoint rejects it). Disabled while no clear modal is open.
  const clearDetail = useArPdc(companyId, clearPdc?.id);

  // Snapshot "now" once per mount for the due cue (mirrors ar.tsx's nowMs idiom;
  // staging Date.now() through state keeps the purity lint happy).
  const [nowMs] = useState<number>(() => Date.now());

  // Cash + bank accounts for the clear modal — codes in the 1110-1119 range
  // (cash equivalents), exactly as RecordReceiptModal picks them.
  const { data: coa = [] } = useCoa(companyId, true);
  const cashAccounts = useMemo(
    () =>
      coa.filter((c) => c.type === 'ASSET' && c.isActive && c.code >= '1110' && c.code < '1120'),
    [coa],
  );

  // Fire-and-forget row actions still surface a server failure — await + alert
  // the parsed message (HttpError.message = problem detail) instead of swallowing.
  async function handleDeposit(pdc: ArPdc) {
    try {
      await deposit.mutateAsync({ id: pdc.id, data: {} });
    } catch (e) {
      window.alert(`นำฝากไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function handleBounce(pdc: ArPdc) {
    const reason = window.prompt('เหตุผลที่เช็คเด้ง');
    if (!reason) return;
    try {
      await bounce.mutateAsync({ id: pdc.id, data: { reason } });
    } catch (e) {
      window.alert(`บันทึกเช็คเด้งไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function handleClearSubmit(data: ClearArPdc) {
    if (!clearPdc) return;
    // On success close + invalidate; on failure the mutateAsync rejects, so we
    // keep the modal OPEN (no setClearPdc(null)) and the modal renders clear.error
    // so the user can fix the bank account / retry.
    try {
      await clear.mutateAsync({ id: clearPdc.id, data });
      setClearPdc(null);
    } catch {
      // error surfaced via `clear.error` inside ClearPdcModal; keep modal open.
    }
  }

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้การค้า', 'เช็ครับ (PDC)']}
        title="เช็ครับลงวันที่ล่วงหน้า (Post-Dated Cheques)"
        actions={
          <>
            <button className="btn" onClick={() => void navigate({ to: '/ar' })}>
              ← กลับลูกหนี้การค้า
            </button>
            <button
              className="btn primary"
              onClick={() => setCreateOpen(true)}
              disabled={!companyId}
            >
              ลงทะเบียนเช็ครับ
            </button>
          </>
        }
      />
      {isPending && (
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          กำลังโหลด…
        </div>
      )}
      {error && (
        <div className="neg" style={{ padding: 24, textAlign: 'center' }}>
          โหลดไม่สำเร็จ: {error.message}
        </div>
      )}
      {data && (
        <PdcList
          pdcs={data}
          nowMs={nowMs}
          onView={(pdc) => void navigate({ to: '/ar', search: { customerId: pdc.customerId } })}
          onDeposit={(pdc) => void handleDeposit(pdc)}
          onClear={(pdc) => {
            // Drop any error from a previous clear before reopening.
            clear.reset();
            setClearPdc(pdc);
          }}
          onBounce={(pdc) => void handleBounce(pdc)}
        />
      )}

      {createOpen && companyId && (
        <CreatePdcModal companyId={companyId} onClose={() => setCreateOpen(false)} />
      )}

      {clearPdc && (
        <ClearPdcModal
          pdc={clearPdc}
          cashAccounts={cashAccounts}
          onSubmit={(data) => void handleClearSubmit(data)}
          onClose={() => setClearPdc(null)}
          submitting={clear.isPending}
          allocations={clearDetail.data?.allocations}
          loadingAllocations={clearDetail.isPending}
          error={clear.error}
        />
      )}
    </div>
  );
}
